import { useState } from "react";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { mediaSrc } from "@/lib/media";

const cardVariants = {
  hidden: { opacity: 0, y: 28, scale: 0.97 },
  show: { opacity: 1, y: 0, scale: 1, transition: { duration: 0.55, ease: [0.22, 1, 0.36, 1] } },
};

const initials = (name = "") =>
  name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("") || "G";

const masonryAspects = ["aspect-[4/5]", "aspect-[3/4]", "aspect-[1/1]", "aspect-[5/6]", "aspect-[4/5]"];

export const GuideVisualCard = ({ guide, index, effect = "cinematic", effectsEnabled = true, aspect }) => {
  const [spot, setSpot] = useState({ x: 50, y: 50 });
  const pointerX = useMotionValue(0.5);
  const pointerY = useMotionValue(0.5);
  const rotateX = useSpring(useTransform(pointerY, [0, 1], [7, -7]), { stiffness: 180, damping: 18 });
  const rotateY = useSpring(useTransform(pointerX, [0, 1], [-7, 7]), { stiffness: 180, damping: 18 });

  const activeEffect = effectsEnabled ? effect : "none";
  const cropX = guide.photo_position_x ?? 50;
  const cropY = guide.photo_position_y ?? 50;
  const cropZoom = guide.photo_zoom ?? 1;
  const isMasonry = activeEffect === "masonry";
  const isTilt = activeEffect === "tilt";
  const isSpotlight = activeEffect === "spotlight";
  const canHoverZoom = ["cinematic", "glass", "spotlight"].includes(activeEffect);
  const aspectClass = isMasonry ? masonryAspects[index % masonryAspects.length] : (aspect || "aspect-[4/5]");

  const handlePointerMove = (event) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const px = Math.min(1, Math.max(0, (event.clientX - rect.left) / rect.width));
    const py = Math.min(1, Math.max(0, (event.clientY - rect.top) / rect.height));
    if (isSpotlight) setSpot({ x: px * 100, y: py * 100 });
    if (isTilt && event.pointerType !== "touch") {
      pointerX.set(px);
      pointerY.set(py);
    }
  };

  const resetPointer = () => {
    setSpot({ x: 50, y: 50 });
    pointerX.set(0.5);
    pointerY.set(0.5);
  };

  const effectClass = {
    cinematic: "border-white/60 bg-white/70 shadow-[0_8px_32px_rgba(26,46,38,0.06)] hover:-translate-y-2 hover:shadow-[0_18px_45px_rgba(200,106,81,0.24)]",
    glass: "border-white/30 bg-white/30 shadow-[0_16px_45px_rgba(26,46,38,0.10)] backdrop-blur-xl hover:-translate-y-3 hover:bg-white/45 hover:shadow-[0_22px_55px_rgba(26,46,38,0.16)]",
    spotlight: "border-white/50 bg-white/50 shadow-[0_8px_32px_rgba(26,46,38,0.07)] group-hover/grid:opacity-55 hover:!opacity-100 hover:-translate-y-2 hover:shadow-[0_18px_45px_rgba(26,46,38,0.16)]",
    masonry: "border-white/50 bg-white/45 shadow-[0_10px_34px_rgba(26,46,38,0.08)] backdrop-blur-sm hover:bg-white/65",
    tilt: "border-white/60 bg-white/70 shadow-[0_12px_38px_rgba(26,46,38,0.10)] [transform-style:preserve-3d] hover:shadow-[0_22px_55px_rgba(200,106,81,0.22)]",
    none: "border-white/60 bg-white/70 shadow-[0_8px_32px_rgba(26,46,38,0.06)]",
  }[activeEffect] || "";

  return (
    <motion.article
      variants={cardVariants}
      data-testid={`guide-card-${index}`}
      data-effect={activeEffect}
      onPointerMove={handlePointerMove}
      onPointerLeave={resetPointer}
      style={isTilt ? { rotateX, rotateY, transformPerspective: 1000 } : undefined}
      className={`group rounded-3xl border p-3 backdrop-blur-md transition-[transform,box-shadow,background-color,border-color,opacity] duration-300 ease-out ${effectClass} ${isMasonry ? "mb-6 inline-block w-full break-inside-avoid md:mb-8" : ""}`}
    >
      <div className={`relative ${aspectClass} overflow-hidden rounded-2xl bg-[#1A2E26]/5`}>
        <div className={`h-full w-full transition-transform duration-700 ease-out ${canHoverZoom ? "group-hover:scale-105" : ""}`}>
          {guide.photo_url ? (
            <img
              src={mediaSrc(guide.photo_url)}
              alt={guide.name}
              loading="lazy"
              data-testid={`guide-photo-${index}`}
              className="h-full w-full object-cover"
              style={{ objectPosition: `${cropX}% ${cropY}%`, transform: `scale(${cropZoom})`, transformOrigin: `${cropX}% ${cropY}%` }}
            />
          ) : (
            <div data-testid={`guide-photo-fallback-${index}`} className="grid h-full w-full place-items-center bg-[#C86A51]/10 text-[#C86A51]">
              <span className="font-serif text-5xl">{initials(guide.name)}</span>
            </div>
          )}
        </div>
        {isSpotlight && (
          <span
            aria-hidden="true"
            data-testid={`guide-spotlight-${index}`}
            className="pointer-events-none absolute inset-0 opacity-0 transition-opacity duration-300 group-hover:opacity-100"
            style={{ background: `radial-gradient(circle at ${spot.x}% ${spot.y}%, rgba(255,255,255,0.34), rgba(200,106,81,0.12) 28%, transparent 58%)` }}
          />
        )}
      </div>
      <div className="px-2 py-4 text-center transition-transform duration-300 group-hover:-translate-y-0.5">
        <h2 data-testid={`guide-name-${index}`} className="font-serif text-xl md:text-2xl leading-tight break-words">
          {guide.name}
        </h2>
        {guide.profession && (
          <p data-testid={`guide-profession-${index}`} className="mt-1.5 text-sm text-[#C86A51] break-words">
            {guide.profession}
          </p>
        )}
        {guide.place && (
          <p data-testid={`guide-place-${index}`} className="mt-0.5 text-xs md:text-sm text-[#1A2E26]/60 break-words">
            {guide.place}
          </p>
        )}
      </div>
    </motion.article>
  );
};
